import { useCallback, useEffect, useRef, useState } from "react";

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
}

export default function useListeningBrowser({
  open,
  ownerCalibrationId,
  onListeningDeleted,
  listeningApi,
}) {
  const [listenings, setListenings] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [selectedListeningId, setSelectedListeningId] = useState(null);
  const [detailedListening, setDetailedListening] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedError, setDetailedError] = useState(null);

  const [componentsData, setComponentsData] = useState(null);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [componentsError, setComponentsError] = useState(null);

  const listRequestIdRef = useRef(0);
  const detailRequestIdRef = useRef(0);
  const componentsRequestIdRef = useRef(0);

  const resetBrowserState = useCallback(() => {
    listRequestIdRef.current += 1;
    detailRequestIdRef.current += 1;
    componentsRequestIdRef.current += 1;

    setListenings([]);
    setListLoading(false);
    setListError(null);

    setSelectedListeningId(null);
    setDetailedListening(null);
    setDetailedLoading(false);
    setDetailedError(null);

    setComponentsData(null);
    setComponentsLoading(false);
    setComponentsError(null);
  }, []);

  const loadListenings = useCallback(async () => {
    const requestId = ++listRequestIdRef.current;

    if (!ownerCalibrationId) {
      setListenings([]);
      return;
    }

    setListLoading(true);
    setListError(null);

    try {
      const response = await listeningApi.getListenings(ownerCalibrationId);
      if (requestId !== listRequestIdRef.current) {
        return;
      }

      const items = response?.data?.listenings ?? response?.data ?? [];
      setListenings(Array.isArray(items) ? items : []);
    } catch (error) {
      if (requestId !== listRequestIdRef.current) {
        return;
      }

      console.error("Failed to load listenings", error);
      setListError(
        getErrorMessage(error, "Nie udało się pobrać listy odsłuchów."),
      );
      setListenings([]);
    } finally {
      if (requestId === listRequestIdRef.current) {
        setListLoading(false);
      }
    }
  }, [listeningApi, ownerCalibrationId]);

  const loadDetailedListening = useCallback(async (listeningId) => {
    const requestId = ++detailRequestIdRef.current;

    if (!listeningId) {
      return;
    }

    setSelectedListeningId(listeningId);
    setDetailedLoading(true);
    setDetailedError(null);
    setComponentsData(null);
    setComponentsError(null);

    try {
      const response = await listeningApi.getListeningDetailed(listeningId);
      if (requestId !== detailRequestIdRef.current) {
        return;
      }

      setDetailedListening(response?.data ?? null);
    } catch (error) {
      if (requestId !== detailRequestIdRef.current) {
        return;
      }

      console.error("Failed to load listening details", error);
      setDetailedError(
        getErrorMessage(error, "Nie udało się pobrać szczegółów odsłuchu."),
      );
      setDetailedListening(null);
    } finally {
      if (requestId === detailRequestIdRef.current) {
        setDetailedLoading(false);
      }
    }
  }, [listeningApi]);

  const loadListeningComponents = useCallback(async () => {
    const requestId = ++componentsRequestIdRef.current;
    const listeningId = detailedListening?.id || selectedListeningId;

    if (!listeningId) {
      return;
    }

    setComponentsLoading(true);
    setComponentsError(null);

    try {
      const response = await listeningApi.getListeningComponents(listeningId);
      if (requestId !== componentsRequestIdRef.current) {
        return;
      }

      setComponentsData(response?.data ?? null);
    } catch (error) {
      if (requestId !== componentsRequestIdRef.current) {
        return;
      }

      console.error("Failed to load listening components", error);
      setComponentsError(
        getErrorMessage(error, "Nie udało się pobrać składowych odsłuchu."),
      );
      setComponentsData(null);
    } finally {
      if (requestId === componentsRequestIdRef.current) {
        setComponentsLoading(false);
      }
    }
  }, [detailedListening, listeningApi, selectedListeningId]);

  const handleDeleteListening = useCallback(
    async (listening) => {
      const listeningId = listening?.id;
      if (!listeningId) {
        return;
      }

      const confirmed = window.confirm(
        `Czy na pewno chcesz usunąć odsłuch "${
          listening?.title || listeningId
        }"?`,
      );

      if (!confirmed) {
        return;
      }

      try {
        await listeningApi.deleteListening(listeningId);
        await loadListenings();

        if (selectedListeningId === listeningId) {
          setSelectedListeningId(null);
          setDetailedListening(null);
          setDetailedError(null);
          setComponentsData(null);
          setComponentsError(null);
        }

        if (onListeningDeleted) {
          Promise.resolve(onListeningDeleted(listening)).catch((error) => {
            console.error("onListeningDeleted failed", error);
          });
        }
      } catch (error) {
        console.error("Failed to delete listening", error);
        setListError(getErrorMessage(error, "Nie udało się usunąć odsłuchu."));
      }
    },
    [listeningApi, loadListenings, onListeningDeleted, selectedListeningId],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    resetBrowserState();
    loadListenings();
  }, [open, ownerCalibrationId, resetBrowserState, loadListenings]);

  return {
    listenings,
    listLoading,
    listError,
    setListError,

    selectedListeningId,
    detailedListening,
    detailedLoading,
    detailedError,
    setDetailedError,

    componentsData,
    componentsLoading,
    componentsError,
    setComponentsError,

    loadListenings,
    loadDetailedListening,
    loadListeningComponents,
    handleDeleteListening,
    resetBrowserState,
  };
}
